import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Tag, Plus, Search, Pencil, Trash2, Loader2, Copy,
} from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";

interface DiscountCode {
  id: string;
  code: string;
  discount_type: string;
  discount_value: number;
  applies_to: string;
  specific_cohort_id: string | null;
  specific_user_id: string | null;
  max_uses: number | null;
  times_used: number;
  expires_at: string | null;
  active: boolean;
  created_at: string;
  stripe_duration: string | null;
  stripe_duration_in_months: number | null;
  stripe_coupon_id: string | null;
}

interface FormData {
  code: string;
  discount_type: string;
  discount_value: string;
  applies_to: string;
  specific_cohort_id: string;
  specific_user_id: string;
  max_uses: string;
  expires_at: string;
  active: boolean;
  stripe_duration: string;
  stripe_duration_in_months: string;
}

const emptyForm: FormData = {
  code: "",
  discount_type: "percentage",
  discount_value: "",
  applies_to: "both",
  specific_cohort_id: "",
  specific_user_id: "",
  max_uses: "",
  expires_at: "",
  active: true,
  stripe_duration: "once",
  stripe_duration_in_months: "3",
};

const DiscountCodesPage = () => {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormData>(emptyForm);

  const { data: codes = [], isLoading } = useQuery<DiscountCode[]>({
    queryKey: ["discount_codes"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("discount_codes")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as DiscountCode[];
    },
  });

  // Cohorts for selector
  const { data: cohorts = [] } = useQuery({
    queryKey: ["public_cohorts_for_discount"],
    queryFn: async () => {
      const { data } = await supabase
        .from("cohorts")
        .select("id, name")
        .eq("is_public", true)
        .order("name");
      return data ?? [];
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (f: FormData) => {
      // Resolve email to user ID if provided
      let resolvedUserId: string | null = null;
      if (f.specific_user_id.trim()) {
        const input = f.specific_user_id.trim();
        // Check if it's already a UUID
        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(input);
        if (isUUID) {
          resolvedUserId = input;
        } else {
          // Look up by email
          const { data: profile } = await supabase
            .from("profiles")
            .select("id")
            .eq("email", input.toLowerCase())
            .maybeSingle();
          if (!profile) throw new Error("user_not_found");
          resolvedUserId = profile.id;
        }
      }

      const payload = {
        code: f.code.trim().toUpperCase(),
        discount_type: f.discount_type,
        discount_value: parseFloat(f.discount_value),
        applies_to: f.applies_to,
        specific_cohort_id: f.specific_cohort_id || null,
        specific_user_id: resolvedUserId,
        max_uses: f.max_uses ? parseInt(f.max_uses) : null,
        expires_at: f.expires_at || null,
        active: f.active,
        stripe_duration: f.stripe_duration || "once",
        stripe_duration_in_months:
          f.stripe_duration === "repeating" ? parseInt(f.stripe_duration_in_months || "1") : null,
      };

      if (editingId) {
        const { error } = await supabase.from("discount_codes").update(payload).eq("id", editingId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("discount_codes").insert(payload);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["discount_codes"] });
      toast.success(editingId ? "Código actualizado" : "Código creado");
      closeDialog();
    },
    onError: (e: any) => {
      if (e?.message === "user_not_found") {
        toast.error("No se encontró un usuario con ese email");
      } else if (e?.message?.includes("duplicate")) {
        toast.error("Ya existe un código con ese nombre");
      } else {
        toast.error("Error al guardar");
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("discount_codes").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["discount_codes"] });
      toast.success("Código eliminado");
    },
  });

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const openEdit = async (c: DiscountCode) => {
    let userEmail = c.specific_user_id ?? "";
    if (c.specific_user_id) {
      const { data: profile } = await supabase
        .from("profiles")
        .select("email")
        .eq("id", c.specific_user_id)
        .maybeSingle();
      userEmail = profile?.email ?? c.specific_user_id;
    }
    setEditingId(c.id);
    setForm({
      code: c.code,
      discount_type: c.discount_type,
      discount_value: String(c.discount_value),
      applies_to: c.applies_to,
      specific_cohort_id: c.specific_cohort_id ?? "",
      specific_user_id: userEmail,
      max_uses: c.max_uses != null ? String(c.max_uses) : "",
      expires_at: c.expires_at ? c.expires_at.slice(0, 16) : "",
      active: c.active,
      stripe_duration: c.stripe_duration ?? "once",
      stripe_duration_in_months: c.stripe_duration_in_months != null ? String(c.stripe_duration_in_months) : "3",
    });
    setDialogOpen(true);
  };

  const filtered = codes.filter(
    (c) => c.code.toLowerCase().includes(search.toLowerCase())
  );

  const generateCode = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let result = "";
    for (let i = 0; i < 8; i++) result += chars[Math.floor(Math.random() * chars.length)];
    setForm((f) => ({ ...f, code: result }));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Tag className="w-6 h-6 text-primary" />
            Códigos de Descuento
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Gestiona códigos de descuento para cohortes y suscripciones
          </p>
        </div>
        <Button onClick={() => { setForm(emptyForm); setEditingId(null); setDialogOpen(true); }} className="gap-2">
          <Plus className="w-4 h-4" /> Nuevo Código
        </Button>
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Buscar por código..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Código</TableHead>
                  <TableHead>Descuento</TableHead>
                  <TableHead>Aplica a</TableHead>
                  <TableHead>Duración (susc.)</TableHead>
                  <TableHead>Cupón Stripe</TableHead>
                  <TableHead>Usos</TableHead>
                  <TableHead>Vencimiento</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="w-[100px]">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                      No hay códigos de descuento
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell>
                        <code className="bg-muted px-2 py-1 rounded text-sm font-mono">
                          {c.code}
                        </code>
                      </TableCell>
                      <TableCell>
                        {c.discount_type === "percentage"
                          ? `${c.discount_value}%`
                          : `$${c.discount_value}`}
                      </TableCell>
                       <TableCell>
                         <Badge variant="secondary" className="text-xs">
                           {c.applies_to === "both" ? "Ambos" : c.applies_to === "cohort" ? "Cohorte" : "Suscripción"}
                         </Badge>
                         {c.specific_cohort_id && (
                           <span className="block text-xs text-muted-foreground mt-1">
                             {cohorts.find(ch => ch.id === c.specific_cohort_id)?.name ?? "Cohorte específica"}
                           </span>
                         )}
                       </TableCell>
                       <TableCell className="text-xs">
                         {c.applies_to === "cohort" ? (
                           <span className="text-muted-foreground">N/A</span>
                         ) : c.stripe_duration === "forever" ? (
                           "Para siempre"
                         ) : c.stripe_duration === "repeating" ? (
                           `${c.stripe_duration_in_months ?? 1} meses`
                         ) : (
                           "1 mes (única)"
                         )}
                       </TableCell>
                       <TableCell>
                         {c.stripe_coupon_id ? (
                           <div className="flex items-center gap-1">
                             <code className="bg-muted px-1.5 py-0.5 rounded text-[10px] font-mono">
                               {c.stripe_coupon_id}
                             </code>
                             <Button
                               variant="ghost"
                               size="icon"
                               className="h-6 w-6"
                               onClick={() => {
                                 navigator.clipboard.writeText(c.stripe_coupon_id!);
                                 toast.success("ID copiado");
                               }}
                             >
                               <Copy className="w-3 h-3" />
                             </Button>
                           </div>
                         ) : (
                           <span className="text-xs text-muted-foreground">— se generará al usarse</span>
                         )}
                       </TableCell>
                      <TableCell>
                        {c.times_used}{c.max_uses != null ? `/${c.max_uses}` : " / ∞"}
                      </TableCell>
                      <TableCell>
                        {c.expires_at
                          ? format(new Date(c.expires_at), "dd MMM yyyy", { locale: es })
                          : "Sin vencimiento"}
                      </TableCell>
                      <TableCell>
                        <Badge variant={c.active ? "default" : "secondary"}>
                          {c.active ? "Activo" : "Inactivo"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(c)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              if (confirm("¿Eliminar este código?")) deleteMutation.mutate(c.id);
                            }}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar Código" : "Nuevo Código de Descuento"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Code */}
            <div>
              <Label>Código *</Label>
              <div className="flex gap-2">
                <Input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  placeholder="DESCUENTO20"
                  className="font-mono"
                />
                <Button variant="outline" size="icon" onClick={generateCode} title="Generar código">
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Type + Value */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tipo de descuento</Label>
                <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Porcentaje (%)</SelectItem>
                    <SelectItem value="fixed">Monto fijo ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Valor *</Label>
                <Input
                   type="number"
                   min="0"
                   max={form.discount_type === "percentage" ? "100" : undefined}
                   value={form.discount_value}
                   onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
                   placeholder={form.discount_type === "percentage" ? "20" : "50"}
                 />
              </div>
            </div>

            {/* Applies to */}
            <div>
              <Label>Aplica a</Label>
              <Select value={form.applies_to} onValueChange={(v) => setForm({ ...form, applies_to: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">Ambos (cohorte y suscripción)</SelectItem>
                  <SelectItem value="cohort">Solo cohortes</SelectItem>
                  <SelectItem value="subscription">Solo suscripción</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Stripe coupon duration (only relevant for subscriptions) */}
            {(form.applies_to === "subscription" || form.applies_to === "both") && (
              <div className="rounded-lg border border-border/60 bg-muted/30 p-3 space-y-3">
                <div>
                  <Label className="text-sm font-semibold">Duración del descuento (suscripciones)</Label>
                  <p className="text-xs text-muted-foreground">
                    Define cuántas facturas mensuales recibirán este descuento en Stripe.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Aplicar</Label>
                    <Select
                      value={form.stripe_duration}
                      onValueChange={(v) => setForm({ ...form, stripe_duration: v })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="once">Una sola vez (1 mes)</SelectItem>
                        <SelectItem value="repeating">Varios meses</SelectItem>
                        <SelectItem value="forever">Para siempre</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {form.stripe_duration === "repeating" && (
                    <div>
                      <Label className="text-xs">Número de meses</Label>
                      <Input
                        type="number"
                        min="1"
                        max="36"
                        value={form.stripe_duration_in_months}
                        onChange={(e) => setForm({ ...form, stripe_duration_in_months: e.target.value })}
                      />
                    </div>
                  )}
                </div>
                {editingId && (
                  <p className="text-[11px] text-muted-foreground">
                    Si cambias estos valores se generará un nuevo cupón en Stripe la próxima vez que alguien use el código.
                  </p>
                )}
              </div>
            )}

            {/* Specific cohort */}
            {(form.applies_to === "cohort" || form.applies_to === "both") && (
              <div>
                <Label>Cohorte específica (opcional)</Label>
                <Select
                  value={form.specific_cohort_id || "any"}
                  onValueChange={(v) => setForm({ ...form, specific_cohort_id: v === "any" ? "" : v })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Cualquier cohorte</SelectItem>
                    {cohorts.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Specific user */}
            <div>
              <Label>Email de usuario específico (opcional)</Label>
              <Input
                value={form.specific_user_id}
                onChange={(e) => setForm({ ...form, specific_user_id: e.target.value })}
                placeholder="usuario@email.com o dejar vacío para todos"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Ingresa el email del usuario o déjalo vacío para que cualquiera lo use
              </p>
            </div>

            {/* Max uses */}
            <div>
              <Label>Máximo de usos (opcional)</Label>
              <Input
                type="number"
                min="1"
                value={form.max_uses}
                onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                placeholder="Sin límite"
              />
            </div>

            {/* Expiration */}
            <div>
              <Label>Fecha de vencimiento (opcional)</Label>
              <Input
                type="datetime-local"
                value={form.expires_at}
                onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
              />
            </div>

            {/* Active */}
            <div className="flex items-center gap-3">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              <Label>Código activo</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>Cancelar</Button>
            <Button
              onClick={() => {
                if (form.discount_type === "percentage" && Number(form.discount_value) > 100) {
                  toast.error("El porcentaje no puede ser mayor a 100");
                  return;
                }
                saveMutation.mutate(form);
              }}
              disabled={!form.code.trim() || !form.discount_value || saveMutation.isPending}
            >
              {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingId ? "Guardar" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DiscountCodesPage;
